<template>
  <div>
    <empty-select-shell
      :is-open="showAssigneeSelector"
      v-bind="{ errorMessage }"
      :style="{
        display: 'flex',
        alignItems: 'center',
      }"
      @click="showUserSelector($event)"
    >
      <span
        v-if="
          !selectedAssigneeIds.length &&
          !hasNoAssigneeSelected &&
          !selectedAdditionalOptionValues.length
        "
        :class="{
          'tw-text-gray-medium': mutedPlaceholder,
        }"
      >
        {{ props.placeholder }}
      </span>
      <span v-else class="tw-text-xs">
        <span
          v-for="option in selectedAdditionalOptions"
          :key="option.value"
          class="tw-mr-1"
        >
          <span class="user-tag" style="top: 0px">
            <octicon
              v-if="!option.icon || !option.iconComponent"
              :height="16"
              :width="16"
              class="user-tag__avatar"
              icon="person"
            />
            <component
              :is="option.iconComponent"
              v-else
              :icon="option.icon"
              :width="16"
              :height="16"
              size="16px"
              class="user-tag__avatar tw-text-gray-medium-dark"
            />

            <span class="tw-mr-1">
              {{ option.label }}
            </span>
          </span>
        </span>
        <span
          v-if="hasNoAssigneeSelected"
          class="tw-relative tw-mr-1 tw-inline-flex tw-items-center tw-bg-gray-light tw-px-1 tw-text-xs tw-text-gray-dark"
          style="height: 20px; border-radius: 20px"
        >
          No Assignee
        </span>
        <span
          v-for="assignee in selectedAssignees"
          :key="assignee.id"
          class="tw-mr-1"
        >
          <user-tag-with-avatar :closable="false" :member="assignee" />
        </span>
      </span>
    </empty-select-shell>
    <multi-user-selector
      ref="multiUserSelectorRef"
      :title="props.title"
      :value="selectedAssignees"
      :no-item-option-selected="hasNoAssigneeSelected"
      :primary-color="Colors.BLUE"
      :no-item-option="includeNoAssigneeOption"
      :use-select-button="false"
      :show-archived-toggle="true"
      :additional-options="referenceOptions"
      :additional-options-value="selectedAdditionalOptionValues"
      @selected="selectAssigneeIds"
      @show="showAssigneeSelector = true"
      @hide="showAssigneeSelector = false"
    />
  </div>
</template>

<script setup lang="ts">
import { InputVariable } from '@/models/common.models';
import { useOrgMembershipsQuery } from '@/utils/users';
import { ref, computed, watch } from 'vue';
import EmptySelectShell from '@/components/core/EmptySelectShell.vue';
import UserTagWithAvatar from '@/components/core/UserTagWithAvatar.vue';
import MultiUserSelector from '@/components/modal/selectors/user/MultiUserSelector.vue';
import isEqual from 'lodash-es/isEqual';
import { UIAutomationTrigger } from '@/models/workflow.models';
import { useAutomations } from '@/utils/automations';
import { Colors, WorkflowAction, WorkflowTrigger } from '@/api';
import { OrganizationMembership } from '@/models/user.models';

interface Props {
  title: string;
  placeholder?: string;
  value?: {
    assigneeIds: Array<string>;
    hasNoAssigneeSelected: boolean;
  };
  additionalOptionsValue?: Array<string>;
  includeNoAssigneeOption?: boolean;
  errorMessage?: string;
  mutedPlaceholder?: boolean;
  // additionalOptions?: Array<{
  //   label: string;
  //   value: string;
  //   icon: string;
  //   iconComponent: string;
  // }>;

  // if passed, these will be used for dynamic/reference options
  trigger?: UIAutomationTrigger;
  action?: Partial<WorkflowAction> & { id: string };
  allActions?: Array<Partial<WorkflowAction> & { id: string }>;
}
interface Emits {
  (
    e: 'assignee-ids-updated',
    value: {
      assigneeIds: Array<string>;
      hasNoAssigneeSelected: boolean;
      additionalOptions?: Array<string>;
    }
  ): void;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select Assignees',
  includeNoAssigneeOption: true,
});
const emit = defineEmits<Emits>();

const { filteredCachedMembers: orgMembers } = useOrgMembershipsQuery();
const {
  workItemContextInputVariables,
  workstreamContextInputVariables,
  workItemContextActionVariables,
  workstreamContextActionVariables,
} = useAutomations();

const referenceOptions = computed(() => {
  const workItemContext = props.trigger?.context.find(
    (context) => context.type === 'workitem'
  );
  const folderContext = props.trigger?.context.find(
    (context) => context.type === 'folder'
  );

  const triggerIsUpdate =
    props.trigger?.workflowTrigger === WorkflowTrigger.ITEM_UPDATED ||
    props.trigger?.workflowTrigger === WorkflowTrigger.FOLDER_UPDATED;

  const triggerIsItemRequest =
    props.trigger?.workflowTrigger === WorkflowTrigger.ITEM_REQUEST_SUBMITTED;

  const ret: Array<{
    label: string;
    value: string;
    icon: string;
    iconComponent: string;
  }> = [];

  const toRetFormat = (value: InputVariable) => {
    return {
      label: (() => {
        let ret: string = value.label;

        if (ret.toLowerCase().endsWith('value')) {
          // Action #1 Item Assignee Value -> Action #1 Item Assignee
          ret = ret.slice(0, -5).trim();
        }

        if (
          !ret.toLowerCase().startsWith('action') &&
          !ret.toLowerCase().startsWith('trigger')
        ) {
          // Item Assignee -> Trigger Item Assignee
          ret = `Trigger ${ret}`;
        }

        return ret;
      })(),
      value: value.reference,
      icon: value.reference.includes('author') ? 'plus-circle' : 'person',
      iconComponent: 'octicon',
    };
  };

  if (workItemContext) {
    ret.push(
      ...workItemContextInputVariables
        .value({
          isUpdate: triggerIsUpdate,
          isItemRequest: triggerIsItemRequest,
          valueTypes: ['user'],
        })
        .map(toRetFormat)
    );
  } else if (folderContext) {
    ret.push(
      ...workstreamContextInputVariables
        .value({
          isUpdate: triggerIsUpdate,
          valueTypes: ['user'],
        })
        .map(toRetFormat)
    );
  }

  let encounteredItemCount = 0,
    encounteredFolderCount = 0;

  let selfActionEncountered = false;
  props.allActions?.forEach((action, idx) => {
    if (selfActionEncountered) return; // can't reference future actions

    const isSelfAction = action.id === props.action?.id;
    if (isSelfAction) selfActionEncountered = true;

    if (
      action.type === 'create-workitem' ||
      action.type === 'update-workitem'
    ) {
      ret.push(
        ...workItemContextActionVariables
          .value({
            actionIndex: encounteredItemCount,
            actionUserReadableCount: idx + 1,
            valueTypes: ['user'],
            isSelfAction,
            isUpdate: action.type === 'update-workitem',
          })
          .map(toRetFormat)
      );
      encounteredItemCount++;
    }

    if (action.type === 'create-folder' || action.type === 'update-folder') {
      ret.push(
        ...workstreamContextActionVariables
          .value({
            actionIndex: encounteredFolderCount,
            actionUserReadableCount: idx + 1,
            valueTypes: ['user'],
            isSelfAction,
            isUpdate: action.type === 'update-folder',
          })
          .map(toRetFormat)
      );
      encounteredFolderCount++;
    }
  });

  return ret;
});

const showAssigneeSelector = ref(false);
const multiUserSelectorRef = ref<InstanceType<typeof MultiUserSelector> | null>(
  null
);

const selectedAssigneeIds = ref<Array<string>>(props.value?.assigneeIds || []);
const selectedAdditionalOptionValues = ref<Array<string>>(
  props.additionalOptionsValue || []
);
const hasNoAssigneeSelected = ref(props.value?.hasNoAssigneeSelected || false);

watch(
  () => props.value,
  (value) => {
    selectedAssigneeIds.value = value?.assigneeIds || [];
    hasNoAssigneeSelected.value = value?.hasNoAssigneeSelected || false;
  },
  { deep: true }
);
watch(
  () => props.additionalOptionsValue,
  (value) => {
    selectedAdditionalOptionValues.value = value || [];
  },
  { deep: true }
);

function selectAssigneeIds(
  users: OrganizationMembership[],
  additionalOptions?: {
    noItemOptionSelected: boolean;
    additionalOptionsValue?: Array<string>;
  }
) {
  selectedAssigneeIds.value = users.map((u) => u.userId);
  hasNoAssigneeSelected.value =
    additionalOptions?.noItemOptionSelected || false;
  selectedAdditionalOptionValues.value =
    additionalOptions?.additionalOptionsValue || [];
}

function showUserSelector(event: MouseEvent) {
  const trigger = event.currentTarget as HTMLElement;
  multiUserSelectorRef.value?.showPopup(trigger);
}

const selectedAssignees = computed(() => {
  const result = orgMembers.value.filter((member) =>
    selectedAssigneeIds.value.includes(member.userId)
  );
  return result;
});

const selectedAdditionalOptions = computed(() => {
  return referenceOptions.value.filter((option) =>
    selectedAdditionalOptionValues.value.includes(option.value)
  );
});

watch(
  () => selectedAssigneeIds.value,
  (value, oldValue) => {
    if (hasNoAssigneeSelected.value && isEqual([...value, null], oldValue)) {
      return;
    }
    if (isEqual(value, oldValue)) return;
    emit('assignee-ids-updated', {
      assigneeIds: value,
      hasNoAssigneeSelected: hasNoAssigneeSelected.value,
      additionalOptions: selectedAdditionalOptionValues.value,
    });
  }
);

watch(
  () => hasNoAssigneeSelected.value,
  (value, oldValue) => {
    if (isEqual(value, oldValue)) return;
    emit('assignee-ids-updated', {
      assigneeIds: selectedAssigneeIds.value,
      hasNoAssigneeSelected: value,
      additionalOptions: selectedAdditionalOptionValues.value,
    });
  }
);

watch(
  () => selectedAdditionalOptionValues.value,
  (value, oldValue) => {
    if (isEqual(value, oldValue)) return;
    emit('assignee-ids-updated', {
      assigneeIds: selectedAssigneeIds.value,
      hasNoAssigneeSelected: hasNoAssigneeSelected.value,
      additionalOptions: value,
    });
  }
);
</script>
